import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { PetFormSection } from "@/components/owners/PetFormSection";
import { Button } from "@akuna/ui";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";

import { usePet, useUpdatePet } from "@/hooks/usePets";
import { useUploadPetPhoto, useDeletePetPhoto } from "@/hooks/useUpload";
import { getLatestPetWeight } from "@/lib/utils/pet-weight";
import {
  isPetDeceased,
  fetchFutureAppointmentsForPet,
  cancelFutureAppointmentsForPet,
  useDeceasedPetStatusId,
} from "@/lib/utils/pet-death";
import type { PetInput } from "@/lib/validations/pet";
import { createPetWizardSchema } from "@/lib/validations/petWizard";

type PetConditionLink = {
  id?: string;
  condition?: { id?: string | null } | null;
};

type QuickEditPetDialogProps = {
  petId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
};

export function QuickEditPetDialog({
  petId,
  open,
  onOpenChange,
  onSaved,
}: QuickEditPetDialogProps) {
  const { t } = useTranslation("medicalRecords");
  const { data: pet, isLoading } = usePet(petId);
  const updatePet = useUpdatePet();
  const uploadPhoto = useUploadPetPhoto();
  const deletePhoto = useDeletePetPhoto();

  const [hasInitialized, setHasInitialized] = useState(false);
  const [initialPhotoUrl, setInitialPhotoUrl] = useState<string | null>(null);
  const deceasedStatusId = useDeceasedPetStatusId();
  const petWizardSchema = useMemo(
    () => createPetWizardSchema(deceasedStatusId),
    [deceasedStatusId],
  );

  const form = useForm<PetInput>({
    resolver: zodResolver(petWizardSchema),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: {
      name: "",
      owner_id: "",
      record_number: "",
      gender_id: "",
      sterilization_id: "",
      birth_date: "",
      species_id: "",
      breed_id: "",
      coat_type_id: "",
      color_id: "",
      status_id: "",
      death_date: "",
      death_cause: "",
      microchip_number: "",
      health_plan: "",
      pedigree_number: "",
      notes: "",
      current_weight: undefined,
      allergies: [],
      temperaments: [],
      photo: null,
      attachments: [],
    },
  });

  // Reset initialized flag when dialog closes so re-opens reload data.
  useEffect(() => {
    if (!open) {
      setHasInitialized(false);
    }
  }, [open]);

  // Track current photo path for delete logic.
  useEffect(() => {
    const photoFilePath = Array.isArray(pet?.photo)
      ? pet.photo[0]?.file_path
      : pet?.photo?.file_path;
    setInitialPhotoUrl(photoFilePath ?? null);
  }, [pet?.photo]);

  // Preload form when pet data arrives.
  useEffect(() => {
    if (!pet || hasInitialized) return;

    const nextAllergies = (pet.allergies ?? [])
      .map((item) => {
        const typed = item as PetConditionLink;
        return typed?.condition?.id ?? typed?.id;
      })
      .filter((id): id is string => typeof id === "string" && Boolean(id));

    const nextTemperaments = (pet.temperaments ?? [])
      .map((item) => {
        const typed = item as PetConditionLink;
        return typed?.condition?.id ?? typed?.id;
      })
      .filter((id): id is string => typeof id === "string" && Boolean(id));

    const photoPath = Array.isArray(pet.photo)
      ? pet.photo[0]?.file_path
      : pet.photo?.file_path;
    const latestWeight = getLatestPetWeight(pet.weights ?? []);
    const latestWeightValue =
      typeof latestWeight?.weight === "number" ? latestWeight.weight : undefined;

    form.reset({
      name: pet.name ?? "",
      owner_id: pet.owner_id ?? "",
      record_number: pet.record_number ?? "",
      gender_id: pet.gender_id ?? "",
      sterilization_id: pet.sterilization_id ?? "",
      birth_date: pet.birth_date ?? "",
      species_id: pet.species_id ?? "",
      breed_id: pet.breed_id ?? "",
      coat_type_id: pet.coat_type_id ?? "",
      color_id: pet.color_id ?? "",
      status_id: pet.status_id ?? "",
      death_date: pet.death_date ?? "",
      death_cause: pet.death_cause ?? "",
      microchip_number: pet.microchip_number ?? "",
      health_plan: pet.health_plan ?? "",
      pedigree_number: pet.pedigree_number ?? "",
      notes: pet.notes ?? "",
      current_weight: latestWeightValue,
      allergies: nextAllergies,
      temperaments: nextTemperaments,
      photo: photoPath ?? null,
      attachments: [],
    });

    setHasInitialized(true);
  }, [pet, form, hasInitialized]);

  const onSubmit = async (data: PetInput) => {
    const currentPhoto = form.getValues("photo");
    const { photo, attachments: _attachments, ...rest } = data;
    const latestWeight = getLatestPetWeight(pet?.weights ?? []);
    const latestWeightValue =
      typeof latestWeight?.weight === "number" ? latestWeight.weight : undefined;
    const submittedWeight =
      typeof rest.current_weight === "number" ? rest.current_weight : undefined;
    const currentWeight =
      submittedWeight !== undefined && submittedWeight !== latestWeightValue
        ? submittedWeight
        : undefined;
    const payload: PetInput = { ...rest, current_weight: currentWeight };

    const isMarkingAsDeceased = !isPetDeceased(pet) && Boolean(data.death_date);
    if (isMarkingAsDeceased) {
      const futureAppointments = await fetchFutureAppointmentsForPet(petId);
      if (futureAppointments.length > 0) {
        const shouldCancel = window.confirm(
          `Este paciente tem ${futureAppointments.length} consulta(s) futura(s). Deseja cancelá-las?`,
        );
        if (shouldCancel) {
          await cancelFutureAppointmentsForPet(petId);
        }
      }
    }

    await updatePet.mutateAsync({ id: petId, input: payload });

    const photoToProcess = currentPhoto ?? photo;
    const shouldDeletePhoto = !photoToProcess && Boolean(initialPhotoUrl);
    if (shouldDeletePhoto) {
      await deletePhoto.mutateAsync(petId);
    } else if (photoToProcess instanceof File) {
      await uploadPhoto.mutateAsync({ petId, file: photoToProcess });
    }

    toast.success(t("quickEdit.petDialog.successToast"));
    onSaved?.();
    onOpenChange(false);
  };

  const isSaving =
    updatePet.isPending || uploadPhoto.isPending || deletePhoto.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent noAnimation className="max-h-[90vh] max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t("quickEdit.petDialog.title")}</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <PetFormSection form={form} index={0} photoAllowReplace petId={petId} />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isSaving}
                >
                  {t("quickEdit.petDialog.cancelButton")}
                </Button>
                <Button type="submit" disabled={isSaving}>
                  {isSaving
                    ? t("quickEdit.petDialog.savingButton")
                    : t("quickEdit.petDialog.saveButton")}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
